import { CheckCircle2, Clock, Lock, Unlink } from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";
import { NextSteps } from "./ContentBlocks.js";
import "./styles.css";

export type StatusVariant = "success" | "invalid" | "expired" | "consumed" | "not_authorised" | "closed";

interface Copy { heading: string; body: string; Icon: typeof CheckCircle2; iconColor: string; }

const COPY: Record<StatusVariant, Copy> = {
  success: {
    heading: "Thank you. Your form has been submitted.",
    body: "We have everything we need for now and will be in touch if anything needs clearing up.",
    Icon: CheckCircle2,
    iconColor: "var(--form-success, #16a34a)",
  },
  invalid: {
    heading: "This link is not valid",
    body: "The form link you followed may be incorrect or out of date.",
    Icon: Unlink,
    iconColor: "var(--text-secondary)",
  },
  expired: {
    heading: "This link has expired",
    body: "Form links expire after a set period. Please contact the person who sent this link to request a new one.",
    Icon: Clock,
    iconColor: "var(--text-secondary)",
  },
  consumed: {
    heading: "This form has already been submitted",
    body: "It looks like this form was submitted previously. If you think this is an error, please get in touch with the person who sent you this link.",
    Icon: CheckCircle2,
    iconColor: "var(--form-success, #16a34a)",
  },
  // Card #2648 — opened after the file it belongs to ended (cancelled, expired or done). Nothing was submitted and
  // nothing can be, so the card says only that — never "so these answers were not submitted", and never a state or code.
  closed: {
    heading: "This form has closed.",
    body: "The file it belongs to has ended, so it no longer takes answers.",
    Icon: Lock,
    iconColor: "var(--text-secondary)",
  },
  not_authorised: {
    heading: "You do not have access to this form",
    body: "You are signed in, but your account cannot open this file. Ask whoever sent you the link to check your access, or sign in with the account they expected.",
    Icon: Lock,
    iconColor: "var(--text-secondary)",
  },
};

export interface StatusCardProps {
  variant: StatusVariant;
  /** KYC-design — declaration copy for the submitted state overrides the defaults. */
  heading?: string;
  body?: string;
  nextSteps?: ReadonlyArray<string>;
  /**
   * Phase 569 — an OPTIONAL action slot, rendered directly under the body. The card authors no
   * words for it: the caller supplies the whole node (the thank-you screen's declared follow-on
   * link or its declared copy). Absent ⇒ the card's DOM is exactly what it was before the slot.
   */
  action?: ReactNode;
}

export function StatusCard({ variant, heading, body, nextSteps, action }: StatusCardProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => { headingRef.current?.focus(); }, [variant]);
  const copy = COPY[variant];
  const Icon = copy.Icon;
  return (
    <div className={`status-card${variant === "success" ? " status-card-success" : ""}`} role="status">
      <Icon size={32} className="status-card-icon" color={copy.iconColor} aria-hidden="true" />
      <h1 ref={headingRef} tabIndex={-1} className="status-card-heading">{heading ?? copy.heading}</h1>
      <p className="status-card-body">{body ?? copy.body}</p>
      {action != null && action !== false && <div className="status-card-action">{action}</div>}
      {nextSteps && nextSteps.length > 0 && <NextSteps steps={nextSteps} />}
    </div>
  );
}
